// bluaIQ — Gantt system task-name bilingual resolver
//
// Every LifecycleTask.task is system-generated and deterministic (default seed,
// Auto-Generate Classic, or jurisdiction pack). Users cannot create or rename
// lifecycle tasks, so the stored text is a stable key. The DB stores ONE task
// string (no nameEn/nameEs), so without this resolver English mode shows the
// Spanish-seeded text and vice-versa.
//
// Strategy (FE-only, no schema, no backfill):
//   displayTaskName(task, isEs)
//     1. jurisdiction tasks → look up by stable task.externalKey
//     2. default-seed + Classic tasks (no externalKey) → look up by the
//        canonical stored text (task.name === task.nameEs === stored `task`)
//     3. otherwise → return the raw stored name (never blank)
//
// Notes are NEVER translated here. This only resolves the task NAME label.

type Bil = { en: string; es: string }

// ── Jurisdiction inspection tasks — keyed by stable externalKey (task.key) ──
// `en` mirrors the pack's English label; `es` is the Spanish translation.
// Shared keys (e.g. `foundation`, `flatwork`) normalize to one canonical label.
export const TASK_LABELS_BY_KEY: Record<string, Bil> = {
  form_survey_uploaded: { en: 'Form board survey uploaded', es: 'Levantamiento de tablas (form board) subido' },
  foundation: { en: 'Foundation inspection', es: 'Inspección de cimentación' },
  foundation_pre_pour: { en: 'Foundation pre-pour / strap-tie hold downs', es: 'Cimentación pre-colado / anclajes' },
  building_sewer_water_gas: { en: 'Building sewer / water / gas outside building', es: 'Drenaje / agua / gas exterior del edificio' },
  plumbing_rough: { en: 'Plumbing rough', es: 'Plomería rough-in' },
  plumbing_rough_in: { en: 'Plumbing rough-in', es: 'Plomería rough-in' },
  plumbing_top_out: { en: 'Plumbing top out / fuel gas', es: 'Plomería top-out / gas combustible' },
  plumbing_top_out_mech_rough: { en: 'Plumbing top out + mechanical rough', es: 'Plomería top-out + mecánico rough-in' },
  top_out_mech_rough: { en: 'Top out / mechanical rough', es: 'Top-out / mecánico rough-in' },
  electrical_rough: { en: 'Electrical rough', es: 'Eléctrico rough-in' },
  mechanical_duct: { en: 'Mechanical duct', es: 'Ducto mecánico' },
  mechanical_rough: { en: 'Mechanical rough', es: 'Mecánico rough-in' },
  frame: { en: 'Frame inspection', es: 'Inspección de estructura' },
  frame_electrical_rough: { en: 'Building frame / electrical rough', es: 'Estructura / eléctrico rough-in' },
  exterior_sheathing_framing_mep: { en: 'Exterior sheathing / framing / MEP', es: 'ZIP/OSB exterior / estructura / MEP' },
  sheathing: { en: 'Sheathing', es: 'Tablero estructural' },
  seconds_framing: { en: '2nds framing', es: 'Estructura (2da etapa)' },
  insulation: { en: 'Insulation', es: 'Aislamiento' },
  drywall: { en: 'Drywall', es: 'Tablaroca' },
  brick_ties: { en: 'Brick ties / wall ties', es: 'Anclajes de ladrillo / muro' },
  brick_ties_pre_stucco: { en: 'Brick ties / pre stucco', es: 'Anclajes de ladrillo / pre-estuco' },
  flatwork: { en: 'Flatwork', es: 'Concreto plano (banquetas/entradas)' },
  irrigation_and_fence: { en: 'Irrigation and fence', es: 'Riego y cerca' },
  trade_finals: { en: 'Trade finals', es: 'Finales de oficios' },
  electrical_final: { en: 'Electrical final', es: 'Final eléctrico' },
  plumbing_mech_final: { en: 'Plumbing / Mechanical final', es: 'Final de plomería / mecánico' },
  pp_final_plumbing_hvac: { en: 'Final plumbing / HVAC', es: 'Final de plomería / HVAC' },
  bp_final_building_electrical: { en: 'Final building / electrical', es: 'Final de edificio / eléctrico' },
  building_final: { en: 'Building final', es: 'Final de edificio' },
  building_final_or_co: { en: 'Building final / CO', es: 'Final de edificio / CO' },
  co_building_final: { en: 'CO / Building final', es: 'CO / final de edificio' },
  meter_release: { en: 'Meter release', es: 'Liberación de medidor' },
  gas_electric_release: { en: 'Gas release and electric release', es: 'Liberación de gas y eléctrico' },
  heat_inspection: { en: 'Electrical / gas meters', es: 'Medidores eléctrico / gas' },
  utility_inspection: { en: 'Utility inspection', es: 'Inspección de servicios públicos' },
  initial_erosion_control: { en: 'Initial erosion control', es: 'Control inicial de erosión' },
  ground_plumbing: { en: 'Rough / sewer / water / gas (ground plumbing)', es: 'Plomería bajo tierra (drenaje / agua / gas)' },
  slab: { en: 'Slab', es: 'Losa' },
  t_pole: { en: 'T-pole', es: 'Poste temporal (T-pole)' },
  temp_pole: { en: 'Temporary pole', es: 'Poste temporal' },
  public_works: { en: 'Public Works', es: 'Obras públicas' },
  permit_defined_inspection_sequence: { en: 'Permit-defined inspection sequence', es: 'Secuencia de inspección definida por el permiso' },
}

// ── Default-seed + Classic tasks — keyed by the canonical stored text ───────
// Seed `name` values are stored Spanish-ish; Classic `task` values are stored
// English. The map normalizes BOTH to formal EN/ES.
export const TASK_LABELS_BY_TEXT: Record<string, Bil> = {
  // ── Default seed (seedLifecycleTasks) — phase 0: Land / Due Diligence ──
  'Definir zona objetivo y criterios': { en: 'Define target area & criteria', es: 'Definir zona objetivo y criterios' },
  'Buscar terreno (Zillow, MLS, wholesalers)': { en: 'Find lot (Zillow, MLS, wholesalers)', es: 'Buscar terreno (Zillow, MLS, mayoristas)' },
  'Evaluar terreno: zonificación, flood zone, utilities': { en: 'Evaluate lot: zoning, flood zone, utilities', es: 'Evaluar terreno: zonificación, zona inundable, servicios' },
  'Due diligence: título, survey, perc test': { en: 'Due diligence: title, survey, perc test', es: 'Debida diligencia: título, levantamiento, prueba de percolación' },
  'Negociar y hacer oferta': { en: 'Negotiate & make offer', es: 'Negociar y hacer oferta' },
  'Abrir escrow / título': { en: 'Open escrow / title', es: 'Abrir escrow / título' },
  'Cerrar compra del terreno': { en: 'Close land purchase', es: 'Cerrar compra del terreno' },
  // phase 1: Plans & Permits
  'Contratar arquitecto / diseñador': { en: 'Hire architect / designer', es: 'Contratar arquitecto / diseñador' },
  'Planos preliminares': { en: 'Preliminary plans', es: 'Planos preliminares' },
  'Revisiones y aprobación': { en: 'Revisions & approval', es: 'Revisiones y aprobación' },
  'Construction drawings para permiso': { en: 'Construction drawings for permit', es: 'Planos de construcción para permiso' },
  'Someter solicitud de permiso': { en: 'Submit permit application', es: 'Someter solicitud de permiso' },
  'Responder correcciones del plan reviewer': { en: 'Respond to plan reviewer corrections', es: 'Responder correcciones del revisor de planos' },
  'Obtener permiso de construcción': { en: 'Obtain building permit', es: 'Obtener permiso de construcción' },
  // phase 2: Bids, Contracts & Insurance
  'Takeoff de materiales': { en: 'Material takeoff', es: 'Cuantificación de materiales' },
  'Solicitar cotizaciones a proveedores': { en: 'Request supplier quotes', es: 'Solicitar cotizaciones a proveedores' },
  'Recibir y comparar cotizaciones': { en: 'Receive & compare quotes', es: 'Recibir y comparar cotizaciones' },
  'Buscar y entrevistar contratistas': { en: 'Find & interview contractors', es: 'Buscar y entrevistar contratistas' },
  'Solicitar y comparar propuestas': { en: 'Request & compare proposals', es: 'Solicitar y comparar propuestas' },
  'Verificar licencias y seguros': { en: 'Verify licenses & insurance', es: 'Verificar licencias y seguros' },
  'Firmar contratos': { en: 'Sign contracts', es: 'Firmar contratos' },
  "Contratar seguros GL + Builder's Risk": { en: "Hire GL + Builder's Risk insurance", es: "Contratar seguros GL + Builder's Risk" },
  // phase 3: Construction
  'Site work y excavación': { en: 'Site work & excavation', es: 'Trabajo de sitio y excavación' },
  'Foundation': { en: 'Foundation', es: 'Cimentación' },
  'Framing': { en: 'Framing', es: 'Estructura' },
  'Roofing (dried-in)': { en: 'Roofing (dried-in)', es: 'Techo (cerrado)' },
  'Windows & Doors': { en: 'Windows & Doors', es: 'Ventanas y puertas' },
  'Rough Plumbing': { en: 'Plumbing rough-in', es: 'Plomería — rough-in' },
  'Rough Electrical': { en: 'Electrical rough-in', es: 'Eléctrico — rough-in' },
  'Rough HVAC': { en: 'HVAC rough-in', es: 'HVAC — rough-in' },
  'Inspecciones rough-in': { en: 'Rough-in inspections', es: 'Inspecciones rough-in' },
  'Insulation': { en: 'Insulation', es: 'Aislamiento' },
  'Drywall hang, tape, texture': { en: 'Drywall hang, tape, texture', es: 'Tablaroca: colgado, cinta, textura' },
  'Pintura interior': { en: 'Interior paint', es: 'Pintura interior' },
  'Cabinets y countertops': { en: 'Cabinets & countertops', es: 'Gabinetes y cubiertas' },
  'Flooring': { en: 'Flooring', es: 'Pisos' },
  'Fixtures MEP finish': { en: 'MEP finish & fixtures', es: 'Acabados y accesorios MEP' },
  'Exterior y landscaping': { en: 'Exterior & landscaping', es: 'Exterior y paisajismo' },
  'Punch list final': { en: 'Final punch list', es: 'Punch list final' },
  // phase 4: Closeout / Sale
  'Inspección final → Certificate of Occupancy': { en: 'Final inspection → Certificate of Occupancy', es: 'Inspección final → Certificado de Ocupación' },
  'Home inspection (comprador)': { en: 'Home inspection (buyer)', es: 'Inspección del hogar (comprador)' },
  'Appraisal': { en: 'Appraisal', es: 'Avalúo' },
  'Cierre de venta / escrow': { en: 'Sale closing / escrow', es: 'Cierre de venta / escrow' },
  'Documentación post-cierre': { en: 'Post-closing documentation', es: 'Documentación post-cierre' },

  // ── Auto-Generate Classic (stored English `task` descriptions) ──
  'Grading, utilities, temporary power': { en: 'Grading, utilities, temporary power', es: 'Nivelación, servicios, energía temporal' },
  'Slab-on-grade / pier & beam': { en: 'Slab-on-grade / pier & beam', es: 'Losa sobre terreno / pilote y viga' },
  'Walls, roof trusses, sheathing': { en: 'Walls, roof trusses, sheathing', es: 'Estructura de muros, techo y ZIP/OSB' },
  'Shingles, underlayment, flashing': { en: 'Shingles, underlayment, flashing', es: 'Tejas, membrana, tapajuntas' },
  'Plumbing, electrical, HVAC rough': { en: 'Plumbing, electrical, HVAC rough', es: 'Plomería, eléctrico, HVAC rough-in' },
  'Walls R-13, attic R-38': { en: 'Walls R-13, attic R-38', es: 'Muros R-13, ático R-38' },
  'Hang, tape, float, texture': { en: 'Hang, tape, float, texture', es: 'Colgar, encintar, aplanar, texturizar' },
  'Prime + 2 coats walls & ceilings': { en: 'Prime + 2 coats walls & ceilings', es: 'Sellador + 2 capas en muros y techos' },
  'LVP, tile, carpet install': { en: 'LVP, tile, carpet install', es: 'Instalación de LVP, azulejo, alfombra' },
  'Kitchen, bath vanities, countertops': { en: 'Kitchen, bath vanities, countertops', es: 'Cocina, tocadores de baño, cubiertas' },
  'Fixtures, outlets, switches, HVAC startup': { en: 'Fixtures, outlets, switches, HVAC startup', es: 'Accesorios, tomas, interruptores, arranque de HVAC' },
  'Siding, brick, paint, garage door': { en: 'Siding, brick, paint, garage door', es: 'Siding, brick, pintura, puerta de garage' },
  'Sod, irrigation, fencing, driveway': { en: 'Sod, irrigation, fencing, driveway', es: 'Césped, riego, cerca, entrada' },
  'City final, punch list, CO': { en: 'City final, punch list, CO', es: 'Final de ciudad, punch list, CO' },

  // ── Auto-Generate Classic — actionable construction task rows (PR-1) ──
  // One schedulable/blockable activity per row, split out of the bundled scope
  // rows above. The bundled keys above are kept for backward-compat with tasks
  // already stored in existing projects.
  // Site Work
  'Clear and grub lot': { en: 'Clear and grub lot', es: 'Limpieza y desmonte del terreno' },
  'Rough grading': { en: 'Rough grading', es: 'Nivelación preliminar' },
  'Set temporary power (T-pole)': { en: 'Set temporary power (T-pole)', es: 'Instalar poste temporal (T-pole)' },
  'Temporary water and utilities': { en: 'Temporary water and utilities', es: 'Agua y servicios temporales' },
  // Foundation
  'Site layout and form boards': { en: 'Site layout and form boards', es: 'Trazo del sitio y tablas de forma' },
  'Excavation and pad prep': { en: 'Excavation and pad prep', es: 'Excavación y preparación de plataforma' },
  'Foundation forms and reinforcement': { en: 'Foundation forms and reinforcement', es: 'Cimbra y acero de refuerzo' },
  'Underground plumbing rough-in': { en: 'Underground plumbing rough-in', es: 'Plomería subterránea — rough-in' },
  'Foundation pre-pour inspection': { en: 'Foundation pre-pour inspection', es: 'Inspección previa al colado de cimentación' },
  'Pour slab / foundation': { en: 'Pour slab / foundation', es: 'Colado de losa / cimentación' },
  // Framing
  'Wall framing': { en: 'Wall framing', es: 'Estructura de muros' },
  'Roof framing and trusses': { en: 'Roof framing and trusses', es: 'Estructura de techo' },
  'Exterior sheathing': { en: 'Install exterior ZIP/OSB', es: 'Instalar ZIP/OSB exterior' },
  'Frame inspection': { en: 'Frame inspection', es: 'Inspección de estructura' },
  // Roofing
  'Roof underlayment and flashing': { en: 'Roof underlayment and flashing', es: 'Membrana y tapajuntas de techo' },
  'Install shingles': { en: 'Install shingles', es: 'Instalar tejas' },
  'Roof dry-in inspection': { en: 'Roof dry-in inspection', es: 'Inspección de sellado de techo' },
  // MEP Rough-In
  'Rough plumbing': { en: 'Plumbing rough-in', es: 'Plomería — rough-in' },
  'Rough electrical': { en: 'Electrical rough-in', es: 'Eléctrico — rough-in' },
  // 'Rough HVAC' is already defined above (seed construction) — reused as-is.
  'Rough-in inspection': { en: 'Rough-in inspection', es: 'Inspección de rough-in' },
  // Insulation
  'Wall insulation (R-13)': { en: 'Wall insulation (R-13)', es: 'Aislamiento de muros (R-13)' },
  'Attic insulation (R-38)': { en: 'Attic insulation (R-38)', es: 'Aislamiento de ático (R-38)' },
  'Insulation inspection': { en: 'Insulation inspection', es: 'Inspección de aislamiento' },
  // Drywall
  'Hang drywall': { en: 'Hang drywall', es: 'Colgar tablaroca' },
  'Tape and float': { en: 'Tape and float', es: 'Encintado y pasta' },
  'Texture': { en: 'Texture', es: 'Textura' },
  // Interior Paint
  'Prime walls and ceilings': { en: 'Prime walls and ceilings', es: 'Sellador en muros y techos' },
  'Paint walls and ceilings': { en: 'Paint walls and ceilings', es: 'Pintura de muros y techos' },
  'Paint trim and doors': { en: 'Paint trim and doors', es: 'Pintura de molduras y puertas' },
  // Flooring
  'Tile install': { en: 'Tile install', es: 'Instalación de azulejo' },
  'LVP install': { en: 'LVP install', es: 'Instalación de LVP' },
  'Carpet install': { en: 'Carpet install', es: 'Instalación de alfombra' },
  // Cabinets & Counters
  'Install kitchen cabinets': { en: 'Install kitchen cabinets', es: 'Instalar gabinetes de cocina' },
  'Install bath vanities': { en: 'Install bath vanities', es: 'Instalar tocadores de baño' },
  'Fabricate and set countertops': { en: 'Fabricate and set countertops', es: 'Fabricar e instalar cubiertas' },
  // MEP Trim
  'Plumbing fixtures and trim': { en: 'Plumbing fixtures and trim', es: 'Accesorios y acabados de plomería' },
  'Electrical devices and fixtures': { en: 'Electrical devices and fixtures', es: 'Dispositivos y luminarias eléctricas' },
  'HVAC startup and registers': { en: 'HVAC startup and registers', es: 'Arranque de HVAC y registros' },
  // Exterior
  'Install siding / Hardie': { en: 'Install siding / Hardie', es: 'Instalar siding / Hardie' },
  'Install brick or masonry veneer': { en: 'Install brick or masonry veneer', es: 'Instalar brick/veneer de mampostería' },
  'Exterior painting': { en: 'Exterior painting', es: 'Pintura exterior' },
  'Install garage door': { en: 'Install garage door', es: 'Instalar puerta de garage' },
  'Exterior trim and gutters': { en: 'Exterior trim and gutters', es: 'Molduras y canaletas exteriores' },
  // Landscaping
  'Final grade and sod': { en: 'Final grade and sod', es: 'Nivelación final y césped' },
  'Irrigation': { en: 'Irrigation', es: 'Riego' },
  'Fencing': { en: 'Fencing', es: 'Cerca' },
  'Driveway and flatwork': { en: 'Driveway and flatwork', es: 'Entrada y concreto plano' },
  // Final Inspection
  'City final inspection': { en: 'City final inspection', es: 'Inspección final de la ciudad' },
  'Punch list': { en: 'Punch list', es: 'Punch list' },
  'Certificate of Occupancy': { en: 'Certificate of Occupancy', es: 'Certificado de Ocupación' },
}

/**
 * Resolve the bilingual display name for a system-generated Gantt task.
 * jurisdiction (externalKey) → canonical stored text → raw fallback (never blank).
 * Does not translate notes; does not alter any task data.
 */
export function displayTaskName(
  task: { externalKey?: string | null; name?: string | null; nameEs?: string | null },
  isEs: boolean,
): string {
  const lang: keyof Bil = isEs ? 'es' : 'en'
  const key = (task.externalKey || '').trim()
  if (key && TASK_LABELS_BY_KEY[key]) return TASK_LABELS_BY_KEY[key][lang]
  const text = (task.name || task.nameEs || '').trim()
  if (text && TASK_LABELS_BY_TEXT[text]) return TASK_LABELS_BY_TEXT[text][lang]
  // Fallback: raw stored name — never blank (custom/unknown system tasks).
  return task.name || task.nameEs || text
}
